import { useBuffered } from './useBuffered';
import type { ReactNode } from 'react';
import { de, sg } from '../engine/util';
import { PC, PA } from '../engine/rules';

export function Seg({ value, options, onChange, disabled }: { value: any; options: [any, ReactNode][]; onChange: (v: any) => void; disabled?: boolean }) {
  return (
    <div className="seg">
      {options.map(([v, l]) => (
        <button key={String(v)} type="button" aria-pressed={String(value) === String(v)} disabled={disabled} onClick={() => onChange(v)}>{l}</button>
      ))}
    </div>
  );
}

export function Panel({ title, aside, children, className }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={'panel ' + (className || '')}>
      {title && <h3>{title}{aside && <span className="label">{aside}</span>}</h3>}
      {children}
    </div>
  );
}

const ICONS: Record<string, ReactNode> = {
  poll: <path d="M3 20h18M6 16V9M11 16V4M16 16v-6M21 16v-3" />,
  appr: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-5-6.3" /></>,
  budget: <><rect x="3" y="7" width="18" height="12" rx="1.5" /><circle cx="12" cy="13" r="3" /><path d="M3 11h2M19 15h2" /></>,
  debt: <path d="M3 10l9-6 9 6M5 10v9M9 10v9M15 10v9M19 10v9M3 19h18" />,
  gr: <path d="M3 17l6-6 4 4 8-8M15 7h6v6" />,
  al: <><rect x="3" y="7" width="18" height="13" rx="1.5" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" /></>,
  infl: <path d="M12 3c1 4 5 6 5 11a5 5 0 0 1-10 0c0-3 2-4 2-6 1 1 2 2 3 2 0-3-1-5 0-7z" />,
};
export function Icon({ k }: { k: string }) { return <span className="ic" aria-hidden="true"><svg viewBox="0 0 24 24">{ICONS[k]}</svg></span>; }

export function Stat({ label, value, delta, goodUp = true, unit = '', icon, sub, bad }: { label: string; value: string; delta?: number | null; goodUp?: boolean; unit?: string; icon?: string; sub?: string; bad?: boolean }) {
  let d: ReactNode = null;
  if (delta != null && Math.abs(delta) >= .05) { const g = goodUp ? delta > 0 : delta < 0; d = <span className={'d ' + (g ? 'up' : 'down')}>{sg(delta)}{unit}</span>; }
  return (
    <div className="stat">{icon && <Icon k={icon} />}<span className="label">{label}</span><span className={'v' + (bad ? ' down' : '')}>{value}{d}{sub && <span className="d">{sub}</span>}</span></div>
  );
}

export function Bar({ value, mid, coal, color, label, wide }: { value: number; mid?: boolean; coal?: boolean; color?: string; label: ReactNode; wide?: boolean }) {
  return (
    <div className={'grp' + (wide ? ' wide' : '')}><span>{label}</span><div className={'bar' + (mid ? ' mid' : '') + (coal ? ' coal' : '')}><i style={{ width: `${value}%`, background: color }} /></div><span className="num">{Math.round(value)}</span></div>
  );
}

export function Tag({ good, children }: { good?: boolean | null; children: ReactNode }) {
  return <span className={'tag ' + (good == null ? '' : good ? 'up' : 'down')}>{children}</span>;
}

export const initials = (n: string) => n.split(' ').map((x) => x[0]).join('');
export function Avatar({ M, size }: { M: any; size?: 'sm' | 'lg' }) {
  return <span className={'av ' + (size || '')} style={{ background: PC(M.p) }} aria-hidden="true">{initials(M.n)}</span>;
}
export function AvatarP({ id }: { id: string }) { return <span className="av" style={{ background: PC(id) }} aria-hidden="true">{PA(id)}</span>; }
export function Sw({ id }: { id: string }) { return <span className="sw" style={{ background: PC(id) }} />; }

export function Dots({ n }: { n: number }) { return <span className="dots" aria-label={`${n} von 5`}>{'●'.repeat(n)}<span>{'●'.repeat(5 - n)}</span></span>; }

export function Range({ id, label, value, min, max, step, unit, d, onChange }: { id: string; label: string; value: number; min: number; max: number; step: number; unit: string; d?: number; onChange: (v: number) => void }) {
  const [v, setV, flush] = useBuffered(value, onChange, 250);
  return (
    <div className="param">
      <label className="pl" htmlFor={id}><span>{label}</span><span className="num">{de(v, d ?? (step < 1 ? 1 : 0))}{unit}</span></label>
      <input type="range" id={id} min={min} max={max} step={step} value={v} onChange={(e) => setV(+e.target.value)} onPointerUp={flush} onBlur={flush} />
    </div>
  );
}

export function KV({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return <div className="kv">{rows.map(([k, v], i) => <><span key={'k' + i}>{k}</span><b key={'v' + i} className="num">{v}</b></>)}</div>;
}

export function Box({ label, children, warn }: { label: ReactNode; children: ReactNode; warn?: boolean }) {
  return <div className={'box' + (warn ? ' gg' : '')}><span className="label">{label}</span>{children}</div>;
}
